import * as React from 'react';
import styled, { DefaultTheme, css } from 'styled-components';
import { unique, ensureArray } from '@agilepoint/utils-array';
import { memoizeFunction } from '@agilepoint/utils-common';
import { ListIterator } from '../../utils/array';
import { ITagInputProps, ITagInputOption, TagValue } from './tag-input-types';
import { TagInputField, Tag, Picker } from './tag-input-parts';
import { excludeKeys } from '../../utils';
import { TagInputFieldState } from './theme';
import { selectThemeSemantics } from '../../styling';
import { FlexBox } from '../flex-box';
import { Typography } from '../typography';

function prepareTagItem(value: TagValue): ITagInputOption {
    const actualValue = value.toString().trim();
    return {
        text: actualValue,
        value: actualValue
    };
}

function getValue(item: ITagInputOption) {
    return item.value;
}

function getText(item: ITagInputOption) {
    return item.text;
}

function getTrimmedValue(value: string) {
    return value.trim();
}

const excludeEmptyPredicate = (item: ITagInputOption | null): item is ITagInputOption => !!item;

export interface ITagInputState {
    inputFieldValue: string;
    hideCallout: boolean;
    itemsToDisplay: ITagInputOption[];
    selectedItems: ITagInputOption[];
    isFocused: boolean;
    shouldRenderContent: boolean;
    currentEditingItem?: ITagInputOption;
}

const KEYS_TO_LISTEN: { [key: string]: number } = {
    arrowDown: 40,
    arrowUp: 38,
    tab: 9
};

const Placeholder = styled(Typography.Small)`
    position: absolute;
    z-index: 0;
    user-select: none;
    padding-left: ${selectThemeSemantics(({ gap }) => gap.m)};
`;

class TagInputBase extends React.PureComponent<ITagInputProps, ITagInputState> {
    static defaultProps: Partial<ITagInputProps> = {
        allowCustomValues: true
    };

    private getMatchingItems = memoizeFunction((values: TagValue[], items?: ITagInputOption[]) => {
        if (!items) {
            return values.map(prepareTagItem);
        }
        return values.length > 0 ? items.filter(item => this.searchPredicate(item, values.toString())) : [];
    });

    private inputRef = React.createRef<HTMLInputElement>();
    private rootElementRef = React.createRef<HTMLDivElement>();
    private listViewRef = React.createRef<HTMLDivElement>();
    private itemsIterator: ListIterator<ITagInputOption>;

    static getDerivedStateFromProps(props: ITagInputProps) {
        const { value, items = [], allowCustomValues } = props;

        if (value === undefined) {
            return null;
        }

        const newItems = value.map((val) => {
            const item = items.find(i => i.value === val);
            return item !== undefined ? item : (allowCustomValues ? prepareTagItem(val) : null);
        }).filter(excludeEmptyPredicate);

        return {
            selectedItems: unique(newItems, getValue)
        };
    }

    constructor(props: ITagInputProps) {
        super(props);
        const { defaultValue = [], value, items, allowSuggestions } = this.props;
        const selectedValues = ensureArray(value || defaultValue);
        const itemsToDisplay = allowSuggestions && items ? items : [];

        this.state = {
            inputFieldValue: '',
            hideCallout: true,
            selectedItems: this.getMatchingItems(selectedValues, items),
            itemsToDisplay,
            isFocused: true,
            shouldRenderContent: false
        };
        this.itemsIterator = new ListIterator(itemsToDisplay);
    }

    componentDidUpdate(prevProps: ITagInputProps, prevState: ITagInputState) {
        const { items = [] } = this.props;
        const { inputFieldValue, isFocused, currentEditingItem } = this.state;
        const { inputFieldValue: prevInputFieldValue } = prevState;

        if ((prevInputFieldValue !== inputFieldValue && (inputFieldValue.includes(this.getDelimiter())) || !isFocused)) {
            currentEditingItem ? this.onUpdateItem(currentEditingItem, inputFieldValue) : this.updateValue();
        }

        if (prevProps.items !== items) {
            this.itemsIterator.updateList(items);
        }
    }

    componentDidMount() {
        const { current } = this.rootElementRef;
        if (current) {
            this.setState({
                shouldRenderContent: true
            });
        }
    }

    render() {
        const { hidden, ...rest } = this.props;
        const { shouldRenderContent } = this.state;
        const tagInputProps = excludeKeys(rest, 'defaultValue', 'error', 'width', 'height', 'value', 'delimiter', 'items', 'allowCustomValues',
            'allowSuggestions', 'itemsIterator', 'isStarredItem', 'onAddSuggestion', 'onChange', 'onRemoveItem', 'onTagRender', 'onValidateItem', 'searchPredicate', 'onSearchTextChange');
        return hidden ? null : (
            <FlexBox {...tagInputProps}
                onBlur={this.onBlur}
                ref={this.rootElementRef}
                onClick={this.onWrapperClick}
                tabIndex={0}
                alignItems="center"
                inline>
                {
                    shouldRenderContent
                        ? (
                            <>
                                {this.renderContent()}
                                {this.renderTagInputField()}
                                {this.renderPicker()}
                            </>
                        )
                        : null
                }
            </FlexBox>
        );
    }

    private getSelectedItems() {
        const { allowSuggestions } = this.props;
        const { selectedItems, currentEditingItem } = this.state;

        return (allowSuggestions && currentEditingItem)
            ? selectedItems.filter(item => !item.text.includes(currentEditingItem.text))
            : selectedItems;
    }

    private renderTagInputField() {
        const { disabled } = this.props;
        const { inputFieldValue, currentEditingItem } = this.state;
        const showAddButton = this.canRenderAddButton();
        const { current } = this.rootElementRef;
        const rootElementWidth = current!.clientWidth;

        return currentEditingItem ? null :
            (
                <TagInputField
                    inputFieldValue={inputFieldValue}
                    inputRef={this.inputRef}
                    rootElementWidth={rootElementWidth}
                    showAddButton={showAddButton}
                    onKeyDown={this._onKeyDown}
                    onAddItem={this.onAddItem}
                    disabled={disabled}
                    onBlur={this.onBlur}
                    onChange={this.onInputChange}
                />
            );
    }

    private onBlur = ({ currentTarget }: React.FocusEvent) => {
        const { allowSuggestions } = this.props;
        const { hideCallout, currentEditingItem } = this.state;
        const { current } = this.listViewRef;

        if (currentEditingItem || (allowSuggestions && !hideCallout && !currentTarget.contains(current))) {
            return;
        }

        this.updateValue();
    }

    private canRenderAddButton() {
        const { allowSuggestions, allowCustomValues, disabled } = this.props;
        const { inputFieldValue, itemsToDisplay } = this.state;
        if (disabled || !(allowSuggestions && allowCustomValues && inputFieldValue)) {
            return false;
        }
        const doesMatchExists = itemsToDisplay.some(item => this.searchPredicate(item, inputFieldValue, true));
        return !doesMatchExists;
    }

    private onAddItem = () => {
        const { onAddSuggestion, disabled } = this.props;
        const { inputFieldValue } = this.state;

        if (disabled || !inputFieldValue) {
            return;
        }

        const newItem = prepareTagItem(inputFieldValue);
        this.updateValue();

        if (onAddSuggestion) {
            onAddSuggestion(ensureArray(newItem));
        }
    }

    private renderContent() {
        const { placeholder } = this.props;
        return (
            this.shouldRenderPlaceholder()
                ? (
                    <Placeholder>
                        {placeholder}
                    </Placeholder>
                )
                : this.renderTags()
        );
    }

    private shouldRenderPlaceholder() {
        const { selectedItems, inputFieldValue } = this.state;
        return !(selectedItems.length !== 0 || inputFieldValue);
    }

    private renderTags = () => {
        const { selectedItems } = this.state;
        return selectedItems.map(this.renderTag);
    }

    private renderTag = (tagItem: ITagInputOption) => {
        const { onTagRender, disabled } = this.props;
        const { inputFieldValue, isFocused } = this.state;
        const isInEditMode = this.isInEditMode(tagItem);
        const showAddButton = isInEditMode && this.canRenderAddButton();
        const { current } = this.rootElementRef;
        const rootElementWidth = current!.clientWidth;

        if (onTagRender !== undefined) {
            return onTagRender(tagItem);
        }
        return (
            <Tag
                key={tagItem.value}
                item={tagItem}
                disabled={disabled}
                onRemove={this.onRemoveItem}
                rootElementWidth={rootElementWidth}
                isStarredItem={this.isStarredItem(tagItem)}
                onAddItem={this.onAddItem}
                onUpdate={this.onUpdateItem}
                delimiter={this.getDelimiter()}
                showAddButton={showAddButton}
                onEdit={this.onEdit}
                onChange={this._onTagValueChange}
                editorFieldValue={inputFieldValue}
                inputRef={this.inputRef}
                listViewRef={this.listViewRef}
                isFocused={isFocused}
                onKeyDown={this._onKeyDown}
                isInEditMode={isInEditMode}
                preventTriggerChangeOnBlur={this.shouldPreventTriggerChangeOnBlur()}
            />
        );
    }

    private isInEditMode = (tagItem: ITagInputOption) => {
        const { currentEditingItem } = this.state;
        return Boolean(currentEditingItem && currentEditingItem.text === tagItem.text);
    }

    private shouldPreventTriggerChangeOnBlur = () => {
        const { current } = this.listViewRef;
        const { allowSuggestions } = this.props;
        const { hideCallout } = this.state;
        return Boolean(current && allowSuggestions && !hideCallout);
    }

    private onEdit = (item: ITagInputOption) => {
        const { allowSuggestions, items = [] } = this.props;
        const inputFieldValue = item.text;
        const itemsToDisplay = allowSuggestions ? items.filter(itemsObj => this.searchPredicate(itemsObj, inputFieldValue)) : [];
        const hideCallout = !(allowSuggestions && itemsToDisplay.length);

        this.setState({
            itemsToDisplay,
            hideCallout,
            inputFieldValue,
            currentEditingItem: item,
            isFocused: true
        }, () => {
            if (allowSuggestions) {
                this.highlightItem(item.text);
            }
        });
    }

    private _onTagValueChange = (value: string) => {
        const { allowSuggestions } = this.props;
        const itemsToDisplay = this.getItemsToDisplay(value);
        const hideCallout = value ? this.shouldHideCallout(value, itemsToDisplay) : true;

        this.setState({
            itemsToDisplay,
            hideCallout,
            inputFieldValue: value,
            isFocused: true
        }, () => {
            if (allowSuggestions) {
                this.highlightItem(value);
            }
        });
    }

    private isStarredItem(item: ITagInputOption) {
        const { isStarredItem } = this.props;
        return isStarredItem ? isStarredItem(item) : false;
    }

    private onRemoveItem = (itemToRemove: ITagInputOption) => {
        const { onRemoveItem } = this.props;
        const { selectedItems } = this.state;

        if (onRemoveItem) {
            onRemoveItem(itemToRemove);
        }
        const newItems = selectedItems.filter(item => !this.searchPredicate(item, itemToRemove.text, true));
        this._onChange(newItems);

        this.setState({
            hideCallout: true
        });
    }

    private isInControlledMode() {
        const { value } = this.props;
        return value !== undefined;
    }

    private _onChange(selectedItems: ITagInputOption[], newItem?: ITagInputOption | ITagInputOption[]) {
        const { onChange } = this.props;
        const uniqueItems = unique(selectedItems, getText);
        const values = uniqueItems.map(getValue);

        let stateObj = {
            currentEditingItem: undefined,
            inputFieldValue: '',
            hideCallout: true,
            isFocused: true
        };

        if (onChange) {
            onChange(values, newItem);
        }

        if (!this.isInControlledMode()) {
            const validStateValueObj = {
                selectedItems: uniqueItems
            };
            stateObj = { ...stateObj, ...validStateValueObj };
        }

        this.setState(stateObj);
    }

    private _onKeyDown = (ev: React.KeyboardEvent) => {
        const { inputFieldValue, selectedItems } = this.state;
        const { current } = this.listViewRef;

        if (ev.keyCode === 13) {
            this.updateValue();
            return;
        }

        switch (ev.keyCode) {
            case KEYS_TO_LISTEN['arrowDown']:
                ev.preventDefault();
                if (current) {
                    current.focus();
                    this.itemsIterator.next();
                }
                break;
            case KEYS_TO_LISTEN['arrowUp']:
                ev.preventDefault();
                if (current) {
                    current.focus();
                    this.itemsIterator.prev();
                }
                break;
            case KEYS_TO_LISTEN['tab']:
                this.updateValue();
                break;
            default:
                break;
        }

        const lastItem = selectedItems[selectedItems.length - 1];
        if (ev.keyCode === 8 && inputFieldValue === '' && lastItem) {
            this.onRemoveItem(lastItem);
        }
    }

    private onUpdateItem = (item: ITagInputOption, newInputValue: string) => {
        const { onAddSuggestion, allowCustomValues } = this.props;
        const { selectedItems, itemsToDisplay } = this.state;
        const delimiter = this.getDelimiter();
        const { current } = this.inputRef;

        const index = selectedItems.findIndex((selectedItem) => this.searchPredicate(selectedItem, item.text, true));

        if (!newInputValue) {
            return;
        }

        const newSelectedItems = selectedItems.slice();
        const newItems = this.getTagItems(newInputValue);

        if (newItems.length === 0) {
            this.onRemoveItem(item);
            return;
        }

        let newPrepareItem: ITagInputOption | ITagInputOption[] | undefined;

        if (allowCustomValues) {
            newPrepareItem = newItems;
            newSelectedItems.splice(index, 1, ...newPrepareItem);
        } else {
            newPrepareItem = itemsToDisplay.find(i => this.searchPredicate(i, newInputValue));
            if (newPrepareItem) {
                newSelectedItems.splice(index, 1, newPrepareItem);
            }
        }

        this._onChange(newSelectedItems, newPrepareItem);

        if (newInputValue.endsWith(delimiter) && current) {
            current.focus();
        }

        if (onAddSuggestion && newItems.length) {
            onAddSuggestion(newItems);
        }
    }

    private updateValue() {
        const { allowSuggestions, allowCustomValues, onAddSuggestion } = this.props;
        const { inputFieldValue, itemsToDisplay, selectedItems } = this.state;

        if (!inputFieldValue || inputFieldValue.trim() === this.getDelimiter()) {
            return;
        }

        const preparedItems = this.getTagItems(inputFieldValue);

        if (preparedItems.length === 0) {
            return;
        }

        let newPreparedItems;
        let newValues;

        if (allowCustomValues) {
            newPreparedItems = preparedItems;
            newValues = selectedItems.concat(preparedItems);
        } else if (allowSuggestions) {
            newPreparedItems = itemsToDisplay.find(item => this.searchPredicate(item, inputFieldValue));
            newValues = newPreparedItems && [...selectedItems, newPreparedItems];
        }

        this.setState({
            inputFieldValue: '',
            hideCallout: true
        });

        this._onChange(newValues || selectedItems, newPreparedItems);

        if (onAddSuggestion !== undefined && newPreparedItems) {
            onAddSuggestion(ensureArray(newPreparedItems));
        }
        this.resetItemsIterator();
    }

    private getTagItems(value: string) {
        const delimiter = this.getDelimiter();
        const splittedValues = value.split(delimiter).filter(getTrimmedValue);
        return splittedValues.map(prepareTagItem).filter(this.validateItem);
    }

    private onWrapperClick = () => {
        const { current } = this.inputRef;
        if (current === null) {
            return;
        }

        current.focus();
        this.setState({
            isFocused: true
        });
    }

    private onInputChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        const { allowSuggestions, onSearchTextChange } = this.props;
        const itemsToDisplay = this.getItemsToDisplay(value);
        const hideCallout = value ? this.shouldHideCallout(value, itemsToDisplay) : true;
        if (onSearchTextChange) {
            onSearchTextChange(value);
        }
        this.setState({
            itemsToDisplay,
            inputFieldValue: value.trim() === this.getDelimiter() ? '' : value,
            hideCallout
        }, () => {
            if (allowSuggestions) {
                this.highlightItem(value);
            }
        });
    }

    private highlightItem = (value: string) => {
        const { allowCustomValues } = this.props;
        const { itemsToDisplay } = this.state;
        this.itemsIterator.updateList(itemsToDisplay);
        this.itemsIterator.find(item => this.searchPredicate(item, value, allowCustomValues));
    }

    private validateItem = (item: ITagInputOption): boolean => {
        const { onValidateItem } = this.props;
        if (onValidateItem) {
            return onValidateItem(item);
        }
        return true;
    }

    private getDelimiter() {
        const { delimiter = ',' } = this.props;
        return delimiter;
    }

    private canRenderPicker() {
        const { allowSuggestions } = this.props;
        const { itemsToDisplay } = this.state;
        return allowSuggestions && itemsToDisplay.length > 0;
    }

    private renderPicker = () => {
        const { hideCallout, itemsToDisplay } = this.state;
        if (hideCallout && !this.canRenderPicker()) {
            return null;
        }

        const selectedItems = this.getSelectedItems();
        this.itemsIterator.updateList(itemsToDisplay);

        return (
            <Picker
                onDismiss={this.hideTagInputCallout}
                hidden={hideCallout}
                listViewRef={this.listViewRef}
                targetRef={this.rootElementRef}
                items={itemsToDisplay}
                itemsIterator={this.itemsIterator}
                selectedValues={selectedItems}
                onChange={this._onPickerChange}
            />
        );
    }

    private shouldHideCallout = (value: string, itemsToDisplay: ITagInputOption[]) => {
        const { allowSuggestions } = this.props;

        const itemsToHideCallout = itemsToDisplay.find(item => this.searchPredicate(item, value));
        return !(allowSuggestions && itemsToHideCallout);
    }

    private getItemsToDisplay = (value: string) => {
        const selectedItems = this.getSelectedItems();
        return this.getItemsToRender(value, selectedItems);
    }

    private hideTagInputCallout = () => {
        this.setState({
            hideCallout: true,
            isFocused: false
        });
        this.resetItemsIterator();
    }

    private resetItemsIterator() {
        this.itemsIterator.reset();
    }

    private isItemSelected(item: ITagInputOption, selectedItems: ITagInputOption[]): boolean {
        return selectedItems.some(selectedItem => this.searchPredicate(selectedItem, item.text, true));
    }

    private getItemsToRender = (value: string, selectedItems: ITagInputOption[]) => {
        const { items = [] } = this.props;
        const filteredItems = items.filter(item => !this.isItemSelected(item, selectedItems));

        return value ?
            filteredItems.filter(item => this.searchPredicate(item, value, false)) :
            filteredItems;
    }

    private focusInputField() {
        const { current } = this.inputRef;
        if (current) {
            current.focus();
        }
    }

    private _onPickerChange = (selectedValues: string[]) => {
        const { onChange } = this.props;
        const { itemsToDisplay, selectedItems, currentEditingItem } = this.state;
        const selectedItemValues = selectedItems.map(getValue);
        const newSelectedValue = selectedValues[selectedValues.length - 1];
        const newSelectedItemValues = [...selectedItemValues, ...selectedValues];

        const newItem = itemsToDisplay.find(item => item.value === newSelectedValue);

        if (newItem) {
            currentEditingItem ? this.onUpdateItem(currentEditingItem, newSelectedValue) : selectedItems.push(newItem);
        }

        this.setState({
            hideCallout: true,
            inputFieldValue: ''
        });

        if (onChange) {
            onChange(newSelectedItemValues, newItem);
        }
        this.focusInputField();
    }

    private searchPredicate = (item: ITagInputOption, searchText: string, strictCheck?: boolean) => {
        const { searchPredicate } = this.props;
        if (searchPredicate) {
            return searchPredicate(item, searchText);
        }
        const lowerCaseText = item.text.toLowerCase();
        const lowerCaseSearchText = searchText.toLowerCase();
        return strictCheck ? item.text === searchText : lowerCaseText.includes(lowerCaseSearchText);
    }
}

const getTagInputStyleByState = memoizeFunction((state: TagInputFieldState) => {
    const getStyles = (theme: DefaultTheme) => {
        const { tagInput: { colors } } = theme;
        return colors.field[state];
    };
    return css`
        background-color: ${({ theme }) => getStyles(theme).backgroundColor};
        border-color: ${({ theme }) => getStyles(theme).borderColor};
        color: ${({ theme }) => getStyles(theme).foregroundColor};
    `;
});

const getTagInputStyle = memoizeFunction((state: TagInputFieldState) => ({ disabled, error }: ITagInputProps) => getTagInputStyleByState(disabled ? 'disabled' : (error ? 'error' : state)));

export const TagInput = styled(TagInputBase).attrs(() => ({
    className: String('ap-tagInput-wrapper')
})) <ITagInputProps>`
    min-width: 150px;
    min-height: 40px;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    border-style: solid;
    flex-wrap: wrap;
    box-sizing: border-box;
    padding: ${selectThemeSemantics(({ gap }) => gap.l)};
    box-shadow: ${({ theme: { tagInput: { composites: { field } } } }) => field.boxShadow};
    border-radius: ${({ theme: { tagInput: { composites: { field } } } }) => field.borderRadius};
    border-width: ${({ theme: { tagInput: { composites: { field } } } }) => field.borderWidth};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
    ${getTagInputStyle('default')}

    &:hover {
        ${getTagInputStyle('hovered')}
    }

    &:focus-within {
        outline: 0;
        ${getTagInputStyle('focussed')}
    }
`;
